/* sync-v5 */
/* GM-style hero: progress line fills over --slide-dur, animationend advances.
   Bars are clickable; fills pause while pressed / off-screen. */
class HeroPeek extends HTMLElement {
  connectedCallback() {
    this.slidesEl = this.querySelector('.hero-peek__slides');
    this.bars = [...this.querySelectorAll('.hero-peek__bar')];
    this.fills = [...this.querySelectorAll('.hero-peek__bar-fill')];
    this.media = [...this.querySelectorAll('.hero-peek__slide')].map((s) => s.querySelector('video'));
    this.index = 0;

    if (this.bars.length <= 1) {
      this.querySelector('.hero-peek__progress')?.remove();
      this.media[0]?.play().catch(() => {});
      return;
    }

    /* seamless loop lanes (Swiper-style): a clone of the last slide sits
       before the track, a clone of the first after it — advancing past
       either end animates ONE slide into the clone, then transitionend
       silently snaps (no transition) to the real twin. this.media was
       collected above, before the clones entered the DOM, so playback
       wiring only ever sees the real slides */
    const slides = [...this.slidesEl.children];
    this.#lane(slides[slides.length - 1], 'afterbegin');
    this.#lane(slides[0], 'beforeend');
    this.#setPos(1, true); /* HTML renders the track at 0, which now shows the last clone */

    this.slidesEl.addEventListener('transitionend', (e) => {
      if (e.target !== this.slidesEl || e.propertyName !== 'transform') return;
      const n = this.bars.length;
      if (this.#pos === n + 1) this.#setPos(1, true);
      else if (this.#pos === 0) this.#setPos(n, true);
    });

    this.bars.forEach((bar, i) => bar.addEventListener('click', () => this.#go(i)));
    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.#go(this.index - 1));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.#go(this.index + 1));
    this.fills.forEach((fill) => fill.addEventListener('animationend', () => this.#go(this.index + 1)));

    // video slides: the bar tracks real playback progress (timeupdate) and
    // the video's own end advances the show; image slides keep the CSS
    // animation + animationend path
    this.media.forEach((v, vi) => {
      if (!v) return;
      v.addEventListener('timeupdate', () => {
        if (vi !== this.index || !isFinite(v.duration) || v.duration <= 0) return;
        this.fills[vi]?.style.setProperty('transform', `scaleX(${v.currentTime / v.duration})`);
      });
      v.addEventListener('ended', () => {
        if (vi === this.index) this.#go(this.index + 1);
      });
    });

    // gesture switching: finger-follow drag, release past a threshold swipes
    this.addEventListener('pointerdown', (e) => this.#onDown(e));
    this.addEventListener('pointermove', (e) => this.#onMove(e));
    this.addEventListener('pointerup', (e) => this.#onUp(e));
    this.addEventListener('pointercancel', (e) => this.#onUp(e));

    // run only while on screen — data saver on mobile Safari. Re-entering
    // RESUMES the show where it was; only the initial entry starts it (a
    // #go here would restart the active video from zero every time)
    let started = false;
    new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) {
            this.#pause(true, true);
            return;
          }
          if (started) {
            this.media[this.index]?.play().catch(() => {});
            this.#pause(false);
          } else {
            this.#go(this.index);
          }
          started = true;
        }),
      { threshold: 0.25 }
    ).observe(this);
  }

  /* physical track lane: 0 = last-slide clone, 1..n = real slides,
     n+1 = first-slide clone (this.index always addresses real slides) */
  #pos = 1;

  #lane(src, where) {
    const c = src.cloneNode(true);
    c.classList.add('hero-peek__slide--lane');
    c.setAttribute('aria-hidden', 'true');
    c.setAttribute('inert', '');
    /* lane media must never stream: strip the HLS wiring and every source
       so both the global video pass and the browser itself see a static
       poster — playback always lives on the real slides */
    c.querySelectorAll('video').forEach((v) => {
      v.removeAttribute('data-hls');
      v.removeAttribute('autoplay');
      v.preload = 'none';
      v.removeAttribute('src');
      v.querySelectorAll('source').forEach((s) => s.remove());
      v.load();
    });
    this.slidesEl.insertAdjacentElement(where, c);
  }

  #setPos(pos, instant = false) {
    this.#pos = pos;
    if (instant) this.slidesEl.style.transition = 'none';
    this.slidesEl.style.transform = `translateX(-${pos * 100}%)`;
    if (instant) {
      void this.slidesEl.offsetWidth; /* commit the jump, then restore CSS transitions */
      this.slidesEl.style.transition = '';
    }
  }

  #go(i) {
    const n = this.bars.length;
    const prev = this.index;
    this.index = ((i % n) + n) % n;
    /* a move that wraps past an end targets the clone lane, so the slide
       travels ONE step in the swipe direction; transitionend snaps home */
    let pos = this.index + 1;
    if (i > prev && this.index < prev) pos = n + 1;
    else if (i < prev && this.index > prev) pos = 0;
    this.#setPos(pos);
    this.media.forEach((v, vi) => {
      if (!v) return;
      if (vi === this.index) {
        // switching restarts the video from its first frame: reset, then
        // play once the seek has actually landed (play() during a pending
        // seek resumes from the old position in some engines)
        const startPlay = () => v.play().catch(() => {});
        v.pause();
        if (v.currentTime > 0.05) {
          try {
            v.currentTime = 0;
            v.addEventListener('seeked', startPlay, { once: true });
          } catch (err) {
            startPlay();
          }
        } else {
          startPlay();
        }
      } else {
        v.pause();
      }
    });

    this.bars.forEach((b, bi) => {
      b.classList.toggle('is-active', bi === this.index);
      b.setAttribute('aria-current', String(bi === this.index));
    });
    // restart the active fill from zero. Video slides kill the CSS animation
    // entirely — the bar is driven by the video's own timeupdate and the
    // slide advances on the video's ended event; image slides keep the CSS
    // animation with the configured --slide-dur
    const fill = this.fills[this.index];
    const v = this.media[this.index];
    if (fill) {
      fill.style.animationPlayState = '';
      if (v) {
        fill.style.animation = 'none';
        fill.style.transform = 'scaleX(0)';
      } else {
        fill.style.animation = '';
        fill.style.transform = '';
        fill.style.animation = 'none';
        void fill.offsetWidth; /* restart the keyframes from zero */
        fill.style.animation = '';
      }
    }
    // deactivated video bars drop their inline progress so they read empty
    this.fills.forEach((f, fi) => {
      if (fi !== this.index) f.style.transform = '';
    });
  }


  #gesture = { id: null, startX: 0, startY: 0, dx: 0, dragging: false };

  #onDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    /* a gesture starting mid-wrap (transitionend hasn't snapped yet):
       jump to the real twin first so the drag bases on a real slide */
    const n = this.bars.length;
    if (this.#pos === 0) this.#setPos(n, true);
    else if (this.#pos === n + 1) this.#setPos(1, true);
    this.#gesture = { id: e.pointerId, startX: e.clientX, startY: e.clientY, dx: 0, dragging: false };
    this.#pause(true); // freeze the read-line while touching
  }

  #onMove(e) {
    const g = this.#gesture;
    if (g.id !== e.pointerId) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!g.dragging) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        g.dragging = true;
        this.classList.add('is-dragging');
        try { this.setPointerCapture(e.pointerId); } catch (err) {}
      } else {
        return;
      }
    }
    g.dx = dx;
    this.slidesEl.style.transform = `translateX(calc(${-this.#pos * 100}% + ${dx}px))`;
  }

  #onUp(e) {
    const g = this.#gesture;
    if (g.id !== e.pointerId) return;
    this.#gesture = { id: null };
    this.classList.remove('is-dragging');
    this.#pause(false);
    if (!g.dragging) return;
    const past = Math.abs(g.dx) > Math.max(48, this.clientWidth * 0.15);
    if (past) {
      // the drag moved the slides — swallow the click it would trigger
      const kill = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      this.addEventListener('click', kill, { capture: true, once: true });
      setTimeout(() => this.removeEventListener('click', kill, { capture: true }), 60);
      this.#go(g.dx < 0 ? this.index + 1 : this.index - 1);
    } else {
      this.#go(this.index); // snap back and restart the read-line
    }
  }

  #pause(on, offscreen = false) {
    this.fills.forEach((f) => (f.style.animationPlayState = on ? 'paused' : ''));
    if (offscreen) this.media.forEach((v) => v && v.pause());
  }
}
customElements.define('hero-peek', HeroPeek);

/* Panel controller — nav menu & cart drawer share one scrim,
   open exclusively, lock body scroll, close on ESC/outside/inner links. */
const panels = {
  scrim: null,
  header: null,
  defs: [],
  locFromMenu: false, // localization panel was reached from the menu panel
  isLoc(def) {
    return def.openAttr === '[data-loc-open]';
  },
  init() {
    this.scrim = document.querySelector('.nav-scrim');
    this.header = document.querySelector('.site-header');
    this.defs = [
      { el: document.querySelector('.nav-panel'), openAttr: '[data-nav-open]', closeAttr: '[data-nav-close]' },
      { el: document.querySelector('.cart-drawer'), openAttr: '[data-cart-open]', closeAttr: '[data-cart-close]' },
      { el: document.querySelector('.localization-panel'), openAttr: '[data-loc-open]', closeAttr: '' },
      /* 政策面板（移动端菜单底栏入口）：与 Language 面板同一机制，
         返回箭头复用 data-nav-open（打开菜单即互斥收起本面板） */
      { el: document.querySelector('.policy-panel'), openAttr: '[data-nav-policy-open]', closeAttr: '' },
    ].filter((d) => d.el);
  },
  isOpen(def) {
    return def.el.hasAttribute('open');
  },
  open(def) {
    this.defs.filter((d) => d !== def && this.isOpen(d)).forEach((d) => this.close(d, true));
    def.el.setAttribute('open', '');
    document.querySelectorAll(def.openAttr).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    this.scrim?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    /* 面板都在 .site-nav 内：若导航正随滚动隐藏（is-hidden 平移出屏），
       打开面板时必须复位，否则面板随容器一起不可见（如移动端政策栏入口） */
    document.querySelector('.site-nav')?.classList.remove('is-hidden');
    // menu-spawned localization turns the burger into a back arrow
    if (this.isLoc(def)) {
      document.querySelector('.site-nav')?.classList.toggle('is-submenu', this.locFromMenu);
    }
  },
  close(def, silent) {
    def.el.removeAttribute('open');
    document.querySelectorAll(def.openAttr).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (this.isLoc(def)) {
      this.locFromMenu = false;
      document.querySelector('.site-nav')?.classList.remove('is-submenu');
    }
    if (silent) return;
    this.scrim?.setAttribute('aria-hidden', 'true');
    if (!this.defs.some((d) => this.isOpen(d))) document.body.style.overflow = '';
  },
  closeAll() {
    this.defs.filter((d) => this.isOpen(d)).forEach((d) => this.close(d));
  },
  anyOpen() {
    return this.defs.some((d) => this.isOpen(d));
  },
};

/* 面板系统公共 API（2026-10-31）：外部组件（PDP 政策悬浮栏、购买卡退货
   入口等）通过 PD.openPanel(选择器或元素) 直接打开面板，绕过模拟按钮
   点击——移动端浏览器对隐藏元素的合成 click 行为不可靠。返回是否成功。 */
window.PD = window.PD || {};
window.PD.panels = panels;
window.PD.openPanel = (selector) => {
  if (!panels.defs.length) panels.init();
  const el = selector instanceof Element ? selector : document.querySelector(selector);
  const def = panels.defs.find((d) => d.el === el);
  if (!def) return false;
  panels.open(def);
  return true;
};

document.addEventListener('click', (e) => {
  if (!panels.defs.length) panels.init();
  if (!panels.defs.length) return;

  // back arrow: while a menu-spawned localization submenu is showing, the
  // burger returns to the menu instead of toggling it
  if (panels.locFromMenu && e.target.closest('[data-nav-open]')) {
    e.preventDefault();
    panels.close(panels.defs.find((d) => panels.isLoc(d)));
    panels.open(panels.defs.find((d) => d.openAttr === '[data-nav-open]'));
    return;
  }

  for (const def of panels.defs) {
    if (e.target.closest(def.openAttr)) {
      e.preventDefault();
      if (panels.isLoc(def)) {
        panels.locFromMenu = !!e.target.closest('.nav-panel');
      }
      panels.isOpen(def) ? panels.close(def) : panels.open(def);
      return;
    }
    if (def.closeAttr && e.target.closest(def.closeAttr)) {
      /* menu links carry data-nav-close to dismiss the panel — let the
         browser navigate (page unload hides the panel anyway); only bare
         close controls (buttons) swallow the click */
      if (!e.target.closest('a[href]')) e.preventDefault();
      panels.close(def);
      return;
    }
  }
  if (e.target.closest('[data-panel-close]')) panels.closeAll();

  // the expanded sheet itself acts as the overlay: tapping any non-interactive
  // part of it (empty glass around the menu/cart content) closes the panel
  const onInteractive = e.target.closest('a, button, input, select, textarea, form, label');
  if (panels.anyOpen() && e.target.closest('.site-nav__sheet') && !onInteractive) {
    panels.closeAll();
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') panels.closeAll();
});

/* Header: slide up out of view on scroll down, return on scroll up.
   Stays visible whenever a panel is open. Also rides the PDP buy bar along
   the footer's top edge (fixed bar with a sticky-like release). */
(() => {
  const header = document.querySelector('.site-nav');
  /* whichever full-screen hero the current page has — homepage slideshow or
     PDP stage — the nav stays white until it scrolls past. The PDP locks the
     nav white for the whole page (per current art direction: every module is
     a dark media screen) */
  const hero = document.querySelector('.hero-peek, .pdp-hero');
  const navWhiteLocked = !!document.querySelector('.pdp-hero, [data-story-page], .bf-hero, .people-hero');
  const buybar = document.querySelector('[data-pdp-buybar]');
  const footer = document.querySelector('.site-footer');
  if (!header) return;
  let lastY = window.scrollY;
  let hidden = false;
  let ticking = false;
  const update = () => {
    const y = window.scrollY;
    if (panels.anyOpen()) {
      if (hidden) {
        hidden = false;
        header.classList.remove('is-hidden');
      }
    } else if (y > lastY + 4 && y > 48 && !hidden) {
      hidden = true;
      header.classList.add('is-hidden');
    } else if (y < lastY - 4 && hidden) {
      hidden = false;
      header.classList.remove('is-hidden');
    }
    /* smart color: white while the hero sits under the navbar, dark text on
       a gray frosted pill after passing it; pages without a hero (product,
       cart, …) live in the dark state permanently */
    header.classList.toggle('is-past-hero', !navWhiteLocked && (!hero || hero.getBoundingClientRect().bottom <= 0));
    /* buy card preview: scroll-up expands the details row, scroll-down
       collapses. With CSS sticky bottom:8px the bar's bottom edge sits at
       innerHeight - 8 while following; the tolerance covers the offset */
    if (buybar && !panels.anyOpen()) {
      const barBottom = buybar.getBoundingClientRect().bottom;
      const isFollowing = barBottom > innerHeight - 20;
      if (isFollowing) {
        if (y < lastY - 2) buybar.classList.add('is-open');
        else if (y > lastY + 4) buybar.classList.remove('is-open');
      } else {
        buybar.classList.remove('is-open');
      }
    }
    lastY = y;
    ticking = false;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  update();
})();

document.addEventListener('change', (e) => {
  // buy card SKU dropdown: price, variant id, label and preview swap
  const select = e.target.closest('[data-variant-select]');
  if (select) {
    const wrap = select.closest('form');
    const data = JSON.parse(wrap?.querySelector('[data-variants-json]')?.textContent || '[]');
    const variant = data.find((v) => String(v.id) === select.value);
    if (variant) {
      const idInput = wrap.querySelector('[data-variant-id]');
      if (idInput) idInput.value = variant.id;
      /* 价格唯一写入点：表单带 data-discount-pct（pdp-buybar 折扣展示）时
         写折后价 + 更新原价删除线；无折扣保持原价（compare-at 删除线不动）。
         折扣联动不能在购买卡里另绑监听（旧版 IIFE 与本监听存在事件顺序
         竞态：表单级监听先于 document 级触发、读到未更新的旧变体 id，
         且本监听随后覆盖折后价 → 「折扣价比原价高」的错位展示） */
      const pct = Number(wrap.dataset.discountPct || 0);
      const priceCents = pct ? Math.round((variant.price * (100 - pct)) / 100) : variant.price;
      const money = Shopify.formatMoney ? Shopify.formatMoney(priceCents) : `$${(priceCents / 100).toFixed(2)}`;
      wrap.querySelectorAll('[data-price]').forEach((el) => (el.textContent = money));
      const was = wrap.querySelector('[data-price-was]');
      if (was && pct) {
        was.textContent = Shopify.formatMoney ? Shopify.formatMoney(variant.price) : `$${(variant.price / 100).toFixed(2)}`;
        was.hidden = false;
      }
      /* display-only cleanup of admin variant naming ("iphone18 ProMax" →
         "iPhone 18 Pro Max"); raw titles stay untouched for alt matching */
      const prettyVariant = (t) =>
        String(t).replace(/ProMax/g, 'Pro Max').replace(/(\d)Pro\b/g, '$1 Pro').replace(/^iphone/i, 'iPhone ');
      const label = wrap.querySelector('[data-option-label]');
      if (label) label.textContent = prettyVariant(variant.title);
      const name = wrap.querySelector('[data-variant-name]');
      if (name) name.textContent = prettyVariant(variant.title);
      // preview image: the variant's own image first, then an alt match
      // (image alt = option value), else keep what's showing
      const preview = wrap.querySelector('[data-preview-img]');
      if (preview) {
        const want = String(variant.title || '').toLowerCase().replace(/\s+/g, '');
        const imgMap = JSON.parse(wrap.querySelector('[data-images-json]')?.textContent || '[]');
        const url =
          variant.featured_image?.src ||
          variant.featured_image?.url ||
          imgMap.find((m) => String(m.alt || '').toLowerCase().replace(/\s+/g, '') === want)?.url;
        if (url) preview.src = url;
      }
      /* 社交证明行（销量/库存）已按 2026-10-30 用户反馈移除——库存随变体
         联动的逻辑一并删除，重新加回时见 pdp-buybar.liquid 顶部注释 */
    }
    return;
  }
});

/* HLS videos: m3u8 primary → MP4 fallback. Both sources are configured
   simultaneously (m3u8 URL field + video picker). Safari/iOS plays native
   HLS and NEVER downloads hls.js; other browsers fetch hls.js on demand
   (PD.loadHls) and play via MSE — pages without videos skip the ~90KB
   gzip entirely. Videos below the fold wait for IO before init, so their
   manifest/fragment fetches don't compete with first-screen resources.
   If the stream can't start within 5s of init (dead URL, CORS block,
   network reset, VPN killing the connection), the video switches to its
   MP4 <source> children (Shopify CDN-hosted, always reachable). Falls
   back only once. */
(() => {
  const M3U8_TIMEOUT_MS = 5000;

  window.PD = window.PD || {};

  /* on-demand hls.js injection — one shared pending promise, so several
     videos arriving at the viewport together still fetch the script once */
  PD.loadHls = () => {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (!window.PD_HLS_URL) return Promise.resolve(null);
    if (!PD._hlsPending) {
      PD._hlsPending = new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = window.PD_HLS_URL;
        s.onload = () => resolve(window.Hls || null);
        s.onerror = () => resolve(null);
        document.head.appendChild(s);
      });
    }
    return PD._hlsPending;
  };

  const wire = (video) => {
    const src = video.dataset.hls;
    if (!src || video._wired) return;
    video._wired = true;

    /* MP4 fallback URLs ride on data-mp4 and are injected as <source> only
       at degradation time — real <source> children in the HTML would let an
       autoplay video start pulling the MP4 before theme.js attaches the
       stream (the pre-JS window) */
    const mp4s = (video.dataset.mp4 || '').split(',').filter(Boolean);

    const fallbackToMp4 = () => {
      if (video._degraded) return;
      video._degraded = true;
      if (video._hls) {
        video._hls.destroy();
        delete video._hls;
      }
      if (!mp4s.length) return;
      mp4s.forEach((u) => {
        const s = document.createElement('source');
        s.src = u;
        s.type = 'video/mp4';
        video.appendChild(s);
      });
      video.removeAttribute('src');
      video.load();
      video.play().catch(() => {});
    };

    /* the 5s degradation clock starts at init (wire time), not page load;
       returns the cancel function for markReady/error paths */
    const armTimeout = () => {
      const timer = setTimeout(() => {
        if (!video._hlsReady) fallbackToMp4();
      }, M3U8_TIMEOUT_MS);
      return () => clearTimeout(timer);
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      /* Safari / iOS: native HLS — zero hls.js download */
      const cancel = armTimeout();
      const markReady = () => {
        video._hlsReady = true;
        cancel();
      };
      video.addEventListener('canplay', markReady, { once: true });
      video.addEventListener('error', () => {
        cancel();
        fallbackToMp4();
      }, { once: true });
      video.src = src;
      video.load();
    } else {
      PD.loadHls().then((Hls) => {
        /* the degradation clock may have fired while hls.js was still
           downloading — don't attach a second stream over the MP4 fallback */
        if (video._degraded) return;
        if (!Hls || !Hls.isSupported()) {
          fallbackToMp4();
          return;
        }
        /* clock starts only now: hls.js itself is in hand, so the 5s budget
           measures manifest+first-fragment latency, not library download */
        const cancel = armTimeout();
        const markReady = () => {
          video._hlsReady = true;
          cancel();
        };
        const hls = new Hls({ enableWorker: true, abrEwmaDefaultEstimate: 12e6 });
        hls.on(Hls.Events.MANIFEST_PARSED, markReady);
        hls.on(Hls.Events.FRAG_BUFFERED, markReady);
        hls.on(Hls.Events.ERROR, (evt, data) => {
          if (data.fatal) {
            cancel();
            fallbackToMp4();
          }
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        /* the autoplay attribute doesn't re-fire after MSE attach — nudge it
           once the manifest is in; _autoNudge gives the hero video the same
           nudge even when it doesn't carry autoplay */
        if (video.hasAttribute('autoplay') || video._autoNudge) {
          hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
        }
        video._hls = hls;
      });
    }
  };
  /* popup videos carry data-hls-manual — they attach on first open
     (window.PD.attachHls) instead of here, so nothing streams while hidden */
  const eager = document.querySelectorAll('video[data-hls]:not([data-hls-manual])');

  /* the first hero slide is visible on page load — don't wait for IO to
     start buffering; the play/pause IO still controls playback */
  if (eager[0]) {
    eager[0]._autoNudge = true;
    wire(eager[0]);
  }

  /* the rest wire only as they approach the viewport (500px prefetch band,
     one-shot per video). No IO in the browser → wire everything up front. */
  if (!('IntersectionObserver' in window)) {
    for (let i = 1; i < eager.length; i++) wire(eager[i]);
  } else if (eager.length > 1) {
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          obs.unobserve(en.target);
          wire(en.target);
        });
      },
      { rootMargin: '0px 0px 500px 0px' }
    );
    for (let i = 1; i < eager.length; i++) io.observe(eager[i]);
  }

  window.PD.attachHls = wire;
})();

/* 公告条（2026-10-31 重构）：由 Header 配置直接驱动——运营在后台填
   「活动标题（多语言，支持 emoji）+ 跳转链接」，配置即渲染、留空不渲染
   DOM（不再有按日期切换的档期日历）。此处只负责两件事：
   ① 文案超宽时无缝跑马灯循环（resize 自适应）；
   ② 关闭按钮（当次浏览隐藏）。 */
(() => {
  const bar = document.querySelector('.announce-bar');
  if (!bar) return;

  bar.querySelector('[data-announce-close]')?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    bar.classList.add('is-hiding');
    setTimeout(() => bar.remove(), 450);
  });

  /* marquee: when the text overflows the bar, duplicate it into a two-copy
     track that flows RIGHT → LEFT seamlessly. Re-measured on resize. */
  const measure = () => {
    bar.classList.remove('is-marquee');
    const el = bar.querySelector('[data-announce-text]');
    if (!el) return;
    el.style.removeProperty('--marquee-duration');
    const plain = el.textContent;
    if (el.scrollWidth > el.clientWidth + 2 || el.children.length === 2) {
      let track = el.querySelector('.announce-ticker');
      if (!track || track.children.length !== 2) {
        el.textContent = '';
        track = document.createElement('span');
        track.className = 'announce-ticker';
        const a = document.createElement('span');
        const b = document.createElement('span');
        b.setAttribute('aria-hidden', 'true');
        a.textContent = plain;
        b.textContent = plain;
        track.append(a, b);
        el.append(track);
      }
      const unit = track.firstElementChild.getBoundingClientRect().width;
      if (unit > 0) {
        /* one copy crosses per loop at a constant ~55px/s */
        el.style.setProperty('--marquee-duration', unit / 55 + 's');
        bar.classList.add('is-marquee');
      }
    }
  };
  requestAnimationFrame(measure);
  window.addEventListener('resize', measure, { passive: true });
})();

/* BFCM countdown — reads data-end (ISO string) on [data-bf-countdown],
   ticks every second, turns red under 4 hours */
document.querySelectorAll('[data-bf-countdown]').forEach((cd) => {
  const end = new Date(cd.dataset.end).getTime();
  if (!isFinite(end)) return;
  const els = {
    d: cd.querySelector('[data-cd-days]'),
    h: cd.querySelector('[data-cd-hours]'),
    m: cd.querySelector('[data-cd-mins]'),
    s: cd.querySelector('[data-cd-secs]'),
  };
  const tick = () => {
    let ms = end - Date.now();
    if (ms <= 0) {
      cd.innerHTML = '<span class="bf-countdown__label">' + (cd.dataset.endedLabel || 'Sale Ended') + '</span>';
      clearInterval(iv);
      return;
    }
    if (ms < 4 * 3600e3) cd.classList.add('is-urgent');
    const s = Math.floor(ms / 1000);
    els.d.textContent = String(Math.floor(s / 86400)).padStart(2, '0');
    els.h.textContent = String(Math.floor(s / 3600) % 24).padStart(2, '0');
    els.m.textContent = String(Math.floor(s / 60) % 60).padStart(2, '0');
    els.s.textContent = String(s % 60).padStart(2, '0');
  };
  tick();
  const iv = setInterval(tick, 1000);
});

/* PDP boot gate reveal (layout/theme.liquid adds .pdp-boot on product
   templates): this file is deferred, so it runs at DOMContentLoaded —
   customElements have upgraded by the time this executes. Reveal after a
   double rAF so the first paint lands fully laid out, killing the
   half-parsed "garbled" flash mobile users saw while the 132KB of Markets
   hreflang links streamed in. */
if (document.documentElement.classList.contains('pdp-boot')) {
  requestAnimationFrame(() =>
    requestAnimationFrame(() => document.documentElement.classList.remove('pdp-boot'))
  );
}

/* Global image skeleton: when any img inside .img-skeleton finishes
   loading, flip the container to .is-loaded (skeleton fades out, image
   fades in). Uses capture-phase load delegation — works for images that
   load at any point, including data-src hydration. */
document.addEventListener(
  'load',
  (e) => {
    if (e.target.tagName === 'IMG') {
      e.target.closest('.img-skeleton')?.classList.add('is-loaded');
    }
  },
  true
);
/* images already complete before JS runs (cached) — flip immediately */
document.querySelectorAll('.img-skeleton img').forEach((img) => {
  if (img.complete && img.naturalWidth > 0) {
    img.closest('.img-skeleton')?.classList.add('is-loaded');
  }
});
/* videos inside skeletons flip on the first decoded frame (loadeddata);
   already-buffered videos (readyState >= 2) flip at once */
document.querySelectorAll('.img-skeleton video').forEach((v) => {
  const flip = () => v.closest('.img-skeleton')?.classList.add('is-loaded');
  if (v.readyState >= 2) {
    flip();
    return;
  }
  v.addEventListener('loadeddata', flip, { once: true });
});

/* Deterministic placeholder paint: each .img-skeleton--ph gets a warm-gray
   tone chosen by hashing its media URL — the same media always resolves to
   the same tone, across reloads and sessions (same algorithm as the photo
   wall's colorFor). Dark variants keep their deep gray. Exposed as
   PD.paintSkeletons(root) so late-hydrated sections can repaint. */
PD.paintSkeletons = (root) => {
  const PH = ['#e7e7e5', '#e3e3e0', '#dfdfdc', '#ebe9e6', '#e0e0dd', '#e5e3e0'];
  const scope = root || document;
  /* root may itself be the skeleton (section JS repaints its own stage) */
  const targets = typeof scope.matches === 'function' && scope.matches('.img-skeleton--ph')
    ? [scope, ...scope.querySelectorAll('.img-skeleton--ph')]
    : scope.querySelectorAll('.img-skeleton--ph');
  targets.forEach((el) => {
    if (el.classList.contains('img-skeleton--dark')) return;
    const img = el.querySelector('img');
    const video = el.querySelector('video');
    const key =
      (img && (img.getAttribute('src') || img.dataset.src || img.currentSrc || '')) ||
      (video && (video.dataset.hls || '')) ||
      '';
    if (!key) return;
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    el.style.setProperty('--skeleton-bg', PH[h % PH.length]);
  });
};
PD.paintSkeletons();

/* PDP content-block videos: paused by default — they play only while on
   screen (10% visible is enough to start; leaving pauses them) */
document.querySelectorAll('.pdp-media video').forEach((v) => {
  if (!('IntersectionObserver' in window)) {
    v.play().catch(() => {});
    return;
  }
  new IntersectionObserver(
    (entries) => entries.forEach((en) => (en.isIntersecting ? v.play().catch(() => {}) : v.pause())),
    { threshold: 0.1 }
  ).observe(v);
});

/* Dot-matrix even distribution (Nothing's algorithm): pick the dot count from
   the container width, then stretch the cell so dots land flush on both edges:
   first dot at the left edge, last at the right, middle evenly spaced. */
(() => {
  const inner = document.querySelector('.dot-matrix-fixed__inner');
  if (!inner) return;
  const BASE_PITCH = 96;
  let raf = 0;
  const fit = () => {
    raf = 0;
    const w = inner.clientWidth;
    const n = Math.max(2, Math.round(w / BASE_PITCH));
    inner.style.setProperty('--cell-size', `${w / n}px`);
  };
  const onResize = () => {
    if (!raf) raf = requestAnimationFrame(fit);
  };
  if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(inner);
  window.addEventListener('resize', onResize, { passive: true });
  fit();
})();


/* sync 23098 */

/* Cart page qty steppers: set the row's updates[] input and submit the form
   (Shopify re-renders the cart natively; input onchange does the same). */
document.addEventListener('click', (e) => {
  const step = e.target.closest('.cart-line__step');
  if (!step) return;
  const qty = step.closest('.cart-line__qty');
  const input = qty?.querySelector('input[name="updates[]"]');
  if (!input) return;
  const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.step, 10));
  if (next === parseInt(input.value, 10)) return;
  input.value = next;
  input.form.submit();
});

/* entry thumbnail videos: once metadata lands, make the media box adopt
   the video's true aspect so the configured width sizes the video and the
   height follows its ratio (no 4:3 cropping) */
document.querySelectorAll('.pdp-entry__video').forEach((v) => {
  const apply = () => {
    if (v.videoWidth > 0) {
      v.closest('.pdp-entry__media')?.style.setProperty('aspect-ratio', v.videoWidth + ' / ' + v.videoHeight);
    }
  };
  if (v.readyState >= 1) apply();
  else v.addEventListener('loadedmetadata', apply, { once: true });
});
